/**
 * Aliangé Skin Analysis - Bundled Script
 * structure: Config -> Engine -> UI -> Init
 */

(function () {
    'use strict';

    // Execution guard: the script is included by quiz-global.liquid site-wide and
    // by the aliange-quiz section on the quiz template. If both ever render (or a
    // duplicate tag slips in), a second execution would double-bind every listener
    // and double-fire quiz events + Klaviyo writes. First execution wins.
    if (window.__aliangeQuizInit) return;
    window.__aliangeQuizInit = true;

    // --- 0. UTM CAPTURE ---
    // Capture campaign UTMs once on load — store in sessionStorage so they survive
    // any in-place navigation before the user completes the quiz. Used to attribute
    // email captures back to paid sources (e.g. the Bing Mature Skin capture pivot).
    const QUIZ_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
    const QUIZ_UTM_COOKIE = 'aliange_quiz_utm';

    // sessionStorage alone is per-TAB and dies with the tab. Ad click on Monday,
    // return direct on Wednesday (or in a second tab) and the quiz completion lands
    // in Klaviyo with zero UTM properties — while GA4 still attributes the session
    // to the campaign via its own lookback. That gap is the likeliest reason only
    // 3 of 7 Bing quiz_complete events could be identity-matched in Klaviyo on
    // 3–16 Aug 2026. Mirroring to a 90-day first-party cookie makes the quiz's
    // attribution last-touch and cross-session, matching GA4's window more closely.
    const QUIZ_UTM_COOKIE_DAYS = 90;

    function readQuizUtmCookie() {
        try {
            const m = document.cookie.match(/(?:^|;\s*)aliange_quiz_utm=([^;]*)/);
            if (!m) return null;
            const parsed = JSON.parse(decodeURIComponent(m[1]));
            return (parsed && typeof parsed === 'object') ? parsed : null;
        } catch (e) { return null; }
    }

    function writeQuizUtmCookie(props) {
        try {
            document.cookie = QUIZ_UTM_COOKIE + '=' + encodeURIComponent(JSON.stringify(props)) +
                '; max-age=' + (QUIZ_UTM_COOKIE_DAYS * 24 * 60 * 60) + '; path=/; SameSite=Lax';
        } catch (e) { /* ignore */ }
    }

    (function captureQuizUtms() {
        let found = null;
        try {
            const params = new URLSearchParams(window.location.search);
            QUIZ_UTM_KEYS.forEach(function (key) {
                const val = params.get(key);
                if (val) {
                    found = found || {};
                    found[key] = val;
                }
            });
        } catch (e) { return; /* URLSearchParams unavailable — skip UTM capture */ }

        if (!found) return;

        // Last touch wins, and the whole set is replaced together — never merge a new
        // campaign's utm_source onto an older campaign's utm_content.
        try {
            QUIZ_UTM_KEYS.forEach(function (key) {
                if (found[key]) sessionStorage.setItem('quiz_' + key, found[key]);
                else sessionStorage.removeItem('quiz_' + key);
            });
        } catch (e) { /* sessionStorage unavailable — cookie still carries it */ }
        writeQuizUtmCookie(found);
    })();

    // Build a properties object from stored UTMs. Additive only: keys with no stored
    // value are omitted entirely, so non-campaign traffic never overwrites existing
    // attribution with nulls. For Bing paid traffic only, utm_content (which carries
    // the Bing ad-group label, e.g. "main"/"core"/"b_hook2") is mirrored to
    // bing_ad_group so Hannah can segment Bing captures by ad group. Gated strictly on
    // utm_source === "bing" — otherwise IG/Meta/Google utm_content values would pollute
    // this Bing-only field (fixed 2026-07-28; was previously ungated).
    function getQuizUtmProps() {
        const props = {};
        try {
            QUIZ_UTM_KEYS.forEach(function (key) {
                const val = sessionStorage.getItem('quiz_' + key);
                if (val) props[key] = val;
            });
        } catch (e) { /* sessionStorage unavailable */ }

        // Fall back to the 90-day cookie only when THIS tab session captured nothing —
        // the two sources are read atomically so a stale set can't be spliced into a
        // fresh one.
        if (!Object.keys(props).length) {
            const stored = readQuizUtmCookie();
            if (stored) {
                QUIZ_UTM_KEYS.forEach(function (key) {
                    if (stored[key]) props[key] = stored[key];
                });
            }
        }
        if (props.utm_content && (props.utm_source || '').toLowerCase() === 'bing') {
            props.bing_ad_group = props.utm_content;
        }
        return props;
    }

    // GA4 event version tag — bump when the quiz question set or flow changes so
    // quiz_start / quiz_step / quiz_complete cohorts stay separable across releases.
    const QUIZ_VERSION = '2026-08-22'; // bumped on the cookie/GA4-session quiz_start dedupe — third permanent series break (7 Aug routing, 8 Aug labelling, 22 Aug dedupe)

    // --- quiz_start SESSION DEDUPE -------------------------------------------
    // The dedupe key used to live in sessionStorage. Android in-app webviews (the
    // Facebook/Instagram browser) hand each page load a clean sessionStorage while
    // GA4's session — which is cookie-based — survives, so ONE GA4 session absorbed
    // three "first" opens. entry_point=auto_open read 3.00 events/session on Meta
    // traffic over 15–21 Aug 2026 (Android Webview 15 events / 5 sessions) against a
    // 1.1 guard, and took the quiz start→complete rate offline from the 22 Aug run.
    // Every other browser read 1.000 — the storage assumption is wrong everywhere and
    // merely VISIBLE there, so this is fixed at the root, not special-cased.
    //
    // Two independent stores, so no single failure can take the metric offline again:
    //   1. GUARD  — a first-party cookie keyed on the GA4 session id. Cookies survive
    //                the page loads that wipe sessionStorage, and the key is
    //                GA4-session-scoped BY CONSTRUCTION rather than by luck.
    //   2. HEDGE  — an open counter in localStorage, sent as open_index on every
    //                quiz_start. If the guard ever fails again, the report still
    //                deduplicates server-side on open_index === 1 and never loses the
    //                ability to publish a rate.
    const QUIZ_GA4_MEASUREMENT_ID = 'G7MH1ENL7Z';
    const QUIZ_START_COOKIE = 'aq_qs';
    const QUIZ_OPEN_INDEX_KEY = 'aliange_quiz_open_index';
    const QUIZ_SESSION_TTL_S = 30 * 60; // mirrors GA4's own 30-minute session timeout

    // The _ga_<measurement id> cookie carries the GA4 session id. Two wire formats
    // exist in the wild — the live site serves GS2 (verified 2026-08-22), GS1 is
    // kept so a Google-side format change cannot silently break the guard:
    //   GS2.1.s1787361911$o71$g0$t...   (current)
    //   GS1.1.1787361911.71....         (legacy)
    function ga4SessionId() {
        try {
            const m = document.cookie.match(
                new RegExp('(?:^|;\\s*)_ga_' + QUIZ_GA4_MEASUREMENT_ID + '=([^;]*)')
            );
            if (!m) return null;
            const val = decodeURIComponent(m[1]);
            const gs2 = val.match(/[.$]s(\d+)(?:[$.]|$)/);
            if (gs2) return gs2[1];
            const gs1 = val.match(/^GS\d\.\d\.(\d+)\./);
            if (gs1) return gs1[1];
        } catch (e) { /* cookie unreadable — fall through to the minted key */ }
        return null;
    }

    // Prefer GA4's own session id. When it is genuinely unavailable (cookie blocked,
    // consent not yet granted), mint a key and keep it in the guard cookie so it is
    // still stable across page loads — a minted key is worth far more than no key.
    // Minted keys are prefixed 'x' so a later upgrade to the real GA4 id is
    // recognisable rather than looking like a brand-new session.
    let _mintedQuizKey = null;
    function quizSessionKey() {
        const gaId = ga4SessionId();
        if (gaId) return 'g' + gaId;
        if (!_mintedQuizKey) {
            const prior = readQuizStartCookie();
            _mintedQuizKey = (prior && typeof prior.k === 'string' && prior.k.charAt(0) === 'x')
                ? prior.k
                : 'x' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
        }
        return _mintedQuizKey;
    }

    function readQuizStartCookie() {
        try {
            const m = document.cookie.match(/(?:^|;\s*)aq_qs=([^;]*)/);
            if (!m) return null;
            const parsed = JSON.parse(decodeURIComponent(m[1]));
            return (parsed && typeof parsed === 'object' && Array.isArray(parsed.e)) ? parsed : null;
        } catch (e) { return null; }
    }

    // Re-written on every fired start so the 30-minute max-age slides with activity,
    // exactly like GA4's session timeout. Returns false when the cookie jar rejects
    // the write, which is the signal to fall back to the sessionStorage guard.
    function writeQuizStartCookie(payload) {
        try {
            document.cookie = QUIZ_START_COOKIE + '=' + encodeURIComponent(JSON.stringify(payload)) +
                '; max-age=' + QUIZ_SESSION_TTL_S + '; path=/; SameSite=Lax';
            return !!readQuizStartCookie();
        } catch (e) { return false; }
    }

    // Open counter — deliberately in a DIFFERENT store from the guard. localStorage
    // is persistent where sessionStorage is per-tab and ephemeral, so it survives the
    // webview page loads that caused this bug. Scoped to the session key so it resets
    // with the GA4 session rather than growing forever.
    function nextQuizOpenIndex(key) {
        const bump = function (store) {
            const raw = store.getItem(QUIZ_OPEN_INDEX_KEY);
            let state = null;
            try { state = raw ? JSON.parse(raw) : null; } catch (e) { state = null; }
            const n = (state && state.k === key && typeof state.n === 'number') ? state.n + 1 : 1;
            store.setItem(QUIZ_OPEN_INDEX_KEY, JSON.stringify({ k: key, n: n }));
            return n;
        };
        try { return bump(window.localStorage); } catch (e) { /* try sessionStorage */ }
        try { return bump(window.sessionStorage); } catch (e) { /* no store at all */ }
        return 1;
    }


    // --- entry_point CARRY-THROUGH (quiz_start -> quiz_complete) --------------
    // quiz_complete shipped without entry_point, so GA4 returns only (not set) for
    // it (verified on the 28 Aug funnel run: 18 (not set) / 5 empty-string, zero
    // real values) and no per-entry_point start->complete rate can be published.
    // The value is known at open time, so it is stamped and carried to completion.
    //
    // Attribution rule: the LAST open wins. A session that auto-opens, is
    // dismissed, then re-opens from the pill and finishes belongs to drawer_pill —
    // both starts already exist as separate denominators under the (session,
    // entry_point) guard, so crediting the completion to the open that produced it
    // is the join the report needs.
    //
    // Two independent stores again, mirroring the dedupe design: sessionStorage
    // (per-tab, survives a reload) plus the guard cookie's own entry list (survives
    // the Android in-app-webview page loads that wipe sessionStorage).
    //
    // Known limit of the cookie fallback ONLY: its list holds entry points that
    // FIRED a start, so a repeat open from an already-seen surface is not appended
    // (appending would break the dedupe guard, which tests membership). If storage
    // is dead AND the last open repeats an earlier surface, the fallback names the
    // wrong one. sessionStorage is the primary and is exact; this degrades in the
    // webview case only, and to a real bucket rather than to (not set).
    const QUIZ_ENTRY_KEY = 'aliange_quiz_entry_point';

    function rememberQuizEntry(entryPoint) {
        try { sessionStorage.setItem(QUIZ_ENTRY_KEY, entryPoint); } catch (e) { /* cookie still carries it */ }
    }

    function recallQuizEntry() {
        try {
            const v = sessionStorage.getItem(QUIZ_ENTRY_KEY);
            if (v) return v;
        } catch (e) { /* fall through to the cookie */ }
        const prior = readQuizStartCookie();
        if (prior && prior.k === quizSessionKey() && prior.e.length) return prior.e[prior.e.length - 1];
        return null;
    }

    // Resolve entry_point for quiz_start. Prefer an explicit data-quiz-entry on the
    // trigger element (set on the surfaces we control); otherwise fall back to ONE
    // deterministic rule — nav/header → drawer_menu, homepage → drawer_homepage,
    // else drawer_other. A single legible derivation, not arbitrary DOM sniffing.
    function entryFromEl(el) {
        try { if (el && el.dataset && el.dataset.quizEntry) return el.dataset.quizEntry; } catch (e) { /* noop */ }
        try { if (el && el.closest && el.closest('header, [role="banner"], nav')) return 'drawer_menu'; } catch (e) { /* noop */ }
        return window.location.pathname === '/' ? 'drawer_homepage' : 'drawer_other';
    }

    // --- 1. CONFIGURATION ---
    const QUIZ_CONFIG = {
        settings: {
            intro_heading: "Expert Skin Analysis",
            intro_subheading: "Identify your unique skin profile and receive a prescribed Aliangé protocol.",
        },
        questions: [
            {
                id: "baseline_skin", // Q1
                text: "By midday, how does your skin most consistently feel without makeup?",
                type: "single",
                options: [
                    { id: "oily_all", text: "Oily across most of the face", weights: { acne: 2, barrier: 1 } },
                    { id: "oily_t", text: "Oily through the T-zone with drier cheeks", weights: { barrier: 2 } },
                    { id: "balanced", text: "Comfortable and balanced", weights: { tolerance: 1 } },
                    { id: "tight_dry", text: "Tight or dry, particularly after cleansing", weights: { barrier: 2, ageing: 1 } },
                    { id: "very_dry", text: "Very dry, rough, or prone to flaking", weights: { barrier: 3, sensitivity: 1 } }
                ]
            },
            {
                id: "primary_priority", // Q2
                text: "If one concern were to be medically prioritised, which would it be?",
                type: "single",
                options: [
                    { id: "breakouts", text: "Active breakouts or congestion", weights: { acne: 3 } },
                    { id: "redness", text: "Persistent redness, irritation, or flushing", weights: { sensitivity: 3, barrier: 2 } },
                    { id: "ageing", text: "Fine lines, loss of firmness, or visible ageing", weights: { ageing: 3 } },
                    { id: "pigment", text: "Dark spots, pigmentation, or uneven tone", weights: { pigment: 3 } },
                    { id: "dullness", text: "General dullness or tired appearance", weights: { ageing: 1, pigment: 1 } }
                ]
            },
            {
                id: "tolerance_reactivity", // Q3
                text: "How does your skin typically respond to active ingredients (e.g. retinoids, acids, vitamin C)?",
                type: "single",
                options: [
                    { id: "tolerates_most", text: "My skin tolerates most actives without issue", weights: { tolerance: 3 } },
                    { id: "mild_tingle", text: "Mild tingling initially, settles quickly", weights: { tolerance: 1 } },
                    { id: "red_itchy", text: "Easily becomes red, itchy, or uncomfortable", weights: { sensitivity: 2 } },
                    { id: "reacts_quickly", text: "Reacts quickly — I avoid actives and keep routines minimal", weights: { sensitivity: 3, tolerance: -3 } }
                ]
            },
            {
                id: "barrier_integrity", // Q4
                text: "After cleansing, does your skin ever feel hot, sting, or appear visibly red?",
                type: "single",
                options: [
                    { id: "never", text: "No, never", weights: { barrier: 0 } },
                    { id: "occasionally", text: "Occasionally, after harsh products", weights: { barrier: 1 } },
                    { id: "frequently", text: "Frequently or daily", weights: { barrier: 3, sensitivity: 3 } }
                ]
            },
            {
                id: "structural_ageing", // Q5
                text: "Which changes are you beginning to notice?",
                type: "single",
                options: [
                    { id: "deep_lines", text: "Deepening smile lines or crow’s feet", weights: { ageing: 2 } },
                    { id: "volume_loss", text: "Loss of volume or firmness", weights: { ageing: 3 } },
                    { id: "texture", text: "Rough texture or enlarged pores", weights: { ageing: 1, barrier: 1 } },
                    { id: "fine_lines", text: "A few fine lines only", weights: { ageing: 1 } },
                    { id: "none", text: "None of the above", weights: { ageing: 0 } }
                ]
            },
            {
                id: "congestion_pattern", // Q6
                text: "How frequently do you experience breakouts?",
                type: "single",
                options: [
                    { id: "rarely", text: "Rarely or never", weights: { acne: 0 } },
                    { id: "hormonal", text: "Occasional hormonal breakouts (jaw/chin)", weights: { acne: 1 } },
                    { id: "ongoing", text: "Ongoing blackheads or congestion", weights: { acne: 2 } },
                    { id: "frequent", text: "Frequent, inflamed lesions", weights: { acne: 3, sensitivity: 1 } }
                ]
            },
            {
                id: "pigmentation", // Q7
                text: "Do you have visible pigmentation concerns?",
                type: "single",
                options: [
                    { id: "stubborn", text: "Stubborn dark patches or melasma", weights: { pigment: 3 } },
                    { id: "mild", text: "Mild sun spots or freckling", weights: { pigment: 1 } },
                    { id: "even", text: "Overall tone is even", weights: { pigment: 0 } }
                ]
            },
            {
                id: "environment", // Q8
                text: "Your daily environment is best described as:",
                type: "single",
                options: [
                    { id: "indoors", text: "Mostly indoors / controlled", weights: { env: 0 } },
                    { id: "urban", text: "Urban / polluted", weights: { env: 2 } },
                    { id: "outdoors", text: "Outdoor / high sun exposure", weights: { env: 2, pigment: 1 } }
                ]
            }
        ],
        // NOTE: price + handle here are FALLBACKS only — showResults() fetches live
        // Shopify data via /products/{handle}.js and prefers it. Keep these in sync
        // with the live PDPs (verified against Shopify 2026-06-10) so the fallback and
        // the Klaviyo Protocol_Price never go stale again.
        protocols: [
            {
                id: "essential",
                title: "Essential Protocol",
                description: "Prescribed based on your skin assessment to restore barrier integrity and reduce reactivity.",
                price: 247.50,
                handle: "essential-protocol",
                step1_handle: "daily-devotion-cleanser"
            },
            {
                id: "the_protocol",
                title: "The Protocol",
                description: "Prescribed based on your skin assessment. A comprehensive system to correct multi-factor signs of ageing and barrier compromise.",
                price: 621,
                handle: "the-protocol",
                step1_handle: "daily-devotion-cleanser"
            },
            {
                id: "pm_protocol",
                title: "PM Protocol",
                description: "Prescribed based on your skin assessment. Focused advanced regeneration for significant textural and ageing concerns.",
                price: 256.50,
                handle: "pm-protocol",
                step1_handle: "daily-devotion-cleanser"
            },
            {
                id: "anti_ageing",
                title: "Mature Skin Protocol",
                description: "Prescribed based on your skin assessment. Targeted correction for fine lines, firmness, and elasticity.",
                price: 409.50,
                handle: "mature-skin-protocol",
                step1_handle: "brightening-serum-1"
            },
            {
                id: "acne",
                title: "Acne Protocol",
                description: "Prescribed based on your skin assessment. Clarifying care to decongest pores and balance oil production.",
                price: 315,
                handle: "acne-protocol",
                step1_handle: "daily-devotion-cleanser"
            },
            {
                id: "am_protocol",
                title: "AM Protocol",
                description: "Prescribed based on your skin assessment. Daily defense and protection against environmental stressors.",
                price: 414,
                handle: "am-protocol-1",
                step1_handle: "daily-devotion-cleanser"
            }
        ]
    };

    // --- 2. LOGIC ENGINE ---
    class QuizEngine {
        constructor(config) {
            this.config = config;
            this.currentStep = 0;
            this.answers = {}; // Map of questionId -> selectedOptionId
            this.scores = {
                barrier: 0,
                inflammation: 0,
                pigment: 0,
                ageing: 0,
                acne: 0,
                sensitivity: 0,
                tolerance: 0,
                env: 0
            };
        }

        get totalSteps() {
            return this.config.questions.length;
        }

        getCurrentQuestion() {
            return this.config.questions[this.currentStep];
        }

        answerQuestion(questionId, optionId) {
            this.answers[questionId] = optionId;
        }

        nextStep() {
            if (this.currentStep < this.totalSteps) {
                this.currentStep++;
                return true;
            }
            return false;
        }

        prevStep() {
            if (this.currentStep > 0) {
                this.currentStep--;
                return true;
            }
            return false;
        }

        calculateScores() {
            // Reset scores
            for (const key in this.scores) {
                this.scores[key] = 0;
            }

            // Iterate through answers
            for (const [qId, optionId] of Object.entries(this.answers)) {
                const question = this.config.questions.find(q => q.id === qId);
                if (!question) continue;

                const selectedOption = question.options.find(o => o.id === optionId);
                if (selectedOption && selectedOption.weights) {
                    for (const [category, value] of Object.entries(selectedOption.weights)) {
                        if (this.scores[category] !== undefined) {
                            this.scores[category] += value;
                        }
                    }
                }
            }
            return this.scores;
        }

        determineResult() {
            const s = this.calculateScores();

            // Helper: Count how many domains are significant (>= 2)
            // Domains: Ageing, Pigment, Acne
            const highConcerns = [s.ageing, s.pigment, s.acne].filter(val => val >= 2).length;

            // --- 1. Essential Protocol (Safety Override) ---
            // Trigger: Sensitivity >= 4 OR Barrier >= 5
            if (s.sensitivity >= 4 || s.barrier >= 5) {
                return this.getProtocol("essential");
            }

            // --- 2. The Protocol (Comprehensive Correction) ---
            // Trigger: At least 2 concern domains >= 2 AND Sensitivity < 3 AND Barrier >= 2
            if (highConcerns >= 2 && s.sensitivity < 3 && s.barrier >= 2) {
                return this.getProtocol("the_protocol");
            }

            // --- 3. PM Protocol (Advanced Regeneration) ---
            // Trigger: Ageing >= 4 AND Acne <= 1 AND Sensitivity < 3 AND Barrier >= 2
            if (s.ageing >= 4 && s.acne <= 1 && s.sensitivity < 3 && s.barrier >= 2) {
                return this.getProtocol("pm_protocol");
            }

            // --- 4. Anti-Ageing Protocol (Targeted) ---
            // Trigger: Ageing >= 3 AND Sensitivity < 3
            if (s.ageing >= 3 && s.sensitivity < 3) {
                return this.getProtocol("anti_ageing");
            }

            // --- 5. Acne Protocol ---
            // Trigger: Acne >= 4 AND Sensitivity < 3
            // (If Sensitivity was >= 3, they would likely hit Essential or be downgraded here logic-wise, 
            // but strict rule says "if sens >= 3 -> downgrade", handled by Priority 1 check mostly).
            if (s.acne >= 4 && s.sensitivity < 3) {
                return this.getProtocol("acne");
            }

            // --- 6. AM Protocol (Preventative / Environmental) ---
            // Trigger: Env >= 2 AND Ageing < 3 AND Acne < 2
            if (s.env >= 2 && s.ageing < 3 && s.acne < 2) {
                return this.getProtocol("am_protocol");
            }

            // Fallback (if no strict match) - usually AM or Anti-Ageing depending on profile
            // Default to AM Protocol for safety/prevention
            return this.getProtocol("am_protocol");
        }

        getProtocol(id) {
            return this.config.protocols.find(p => p.id === id);
        }
    }

    // --- 3. UI RENDERER ---
    // const KLAVIYO_LIST_ID = "YOUR_LIST_ID"; // Use config.settings.klaviyo_list_id instead

    class QuizUI {
        constructor(engine, selectors) {
            this.engine = engine;
            this.selectors = selectors;

            this.drawer = document.querySelector(selectors.drawer);
            this.content = document.querySelector(selectors.content);
            this.progressBar = document.querySelector(selectors.progressBar);
            this.nextBtn = document.querySelector(selectors.nextBtn);
            this.prevBtn = document.querySelector(selectors.prevBtn);
            // Select ALL close triggers (The X button, the Overlay, and the Backdrop)
            this.closeBtns = document.querySelectorAll('.close-btn, .quiz-overlay, .quiz-backdrop, #close-quiz-btn');
            this.startBtn = document.querySelector(selectors.startBtn);
        }

        init() {
            this.bindEvents();
        }

        bindEvents() {
            // 1. Internal "Start" Button
            if (this.startBtn) {
                this.startBtn.addEventListener('click', (e) => {
                    // Synthetic/proxy clicks (e.g. an old cached aliange-quiz.liquid
                    // proxy) must not open a second time as 'page' (2026-10-20).
                    if (!e.isTrusted) return;
                    this.openQuiz('page');
                });
            }

            // 2. GLOBAL TRIGGER
            document.addEventListener('click', (e) => {
                // 2a. Explicit opt-in trigger.
                // .open-skin-quiz is the legacy class. [data-open-quiz] is the
                // quiz-page hero CTA (BEGIN SKIN ANALYSIS). Its listener used to
                // live in sections/aliange-quiz.liquid, which the GitHub connector
                // does not sync (AUD-015), so on /pages/quiz the click only set
                // #quiz. Handle it here, in the file that does sync. Runs before
                // 2b so a hero href of /pages/quiz cannot open the drawer twice.
                const quizTrigger = (e.target && e.target.closest)
                    ? e.target.closest('.open-skin-quiz, [data-open-quiz]')
                    : null;
                if (quizTrigger) {
                    e.preventDefault();
                    // This click is handled here. Clear the early-tap flag so the
                    // init path below cannot open a second time for the same tap.
                    window.__aliangeQuizPending = '';
                    this.openQuiz(entryFromEl(quizTrigger));
                    return;
                }

                // 2b. Any link to the quiz page (hero CTA, nav, footer, etc.) opens
                // the drawer in place instead of navigating. Respect modifier/middle
                // clicks (new tab) and target=_blank so those still load the full page.
                const quizLink = e.target.closest('a[href*="/pages/quiz"]');
                if (quizLink && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0 && quizLink.target !== '_blank') {
                    let path;
                    try { path = new URL(quizLink.href, window.location.origin).pathname; }
                    catch (err) { path = ''; }
                    if (path.replace(/\/+$/, '') === '/pages/quiz') {
                        e.preventDefault();
                        this.openQuiz(entryFromEl(quizLink));
                        return;
                    }
                }

                // 2c. A CTA that NAVIGATES to a quiz-open URL on another path
                // (e.g. the concern/pillar-page "/?open-quiz=true" buttons). The
                // click is volitional, but the destination page only sees
                // ?open-quiz=true and would otherwise classify it as auto_open.
                // Stamp the resolved entry onto the destination so the trigger
                // survives the navigation — same mechanism the popup fallback
                // uses. Mutating href (rather than preventDefault + location) keeps
                // modifier/middle-click new-tab behaviour intact.
                const openLink = e.target.closest('a[href*="open-quiz=true"]');
                if (openLink) {
                    try {
                        const dest = new URL(openLink.href, window.location.origin);
                        if (!dest.searchParams.get('quiz_entry')) {
                            dest.searchParams.set('quiz_entry', entryFromEl(openLink));
                            openLink.href = dest.toString();
                        }
                    } catch (err) { /* unparseable href — leave the link alone */ }
                }
            });

            // 3. AUTO-OPEN — the drawer opens with NO user gesture, because the
            // landing URL asked for it (paid traffic has been routed to
            // ?open-quiz=true since 7 Aug 2026). This is a distinct population from
            // a volitional open and must be labelled as such, NOT suppressed: it is
            // the denominator for "of the people we showed the quiz to, how many
            // finished". An explicit quiz_entry in the URL always wins — that means
            // a click stamped it (popup fallback, or a 2c cross-page CTA), so the
            // open is volitional and keeps its own trigger.
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('open-quiz') === 'true') {
                const autoEntry = urlParams.get('quiz_entry') || 'auto_open';
                setTimeout(() => this.openQuiz(autoEntry), 500);
            }

            // 4. Close Triggers (Iterate all found)
            this.closeBtns.forEach(btn => {
                btn.addEventListener('click', () => {
                    this.closeQuiz();
                });
            });

            this.nextBtn.addEventListener('click', () => {
                const currentQ = this.engine.getCurrentQuestion();
                if (this.engine.currentStep < this.engine.totalSteps) {
                    if (!this.engine.answers[currentQ.id]) {
                        alert("Please select an option to move forward.");
                        return;
                    }
                }

                if (this.engine.currentStep < this.engine.totalSteps - 1) {
                    this.engine.nextStep();
                    this.renderStep();
                } else {
                    this.renderEmailGate();
                }
            });

            this.prevBtn.addEventListener('click', () => {
                this.engine.prevStep();
                this.renderStep();
            });

            this.content.addEventListener('click', (e) => {
                if (e.target.classList.contains('option-btn')) {
                    const questionId = e.target.dataset.questionId;
                    const optionId = e.target.dataset.optionId;
                    this.engine.answerQuestion(questionId, optionId);

                    document.querySelectorAll('.option-btn').forEach(btn => btn.classList.remove('selected'));
                    e.target.classList.add('selected');

                    // Auto-advance
                    this.handleAutoAdvance();
                }
            });
        }

        handleAutoAdvance() {
            // 2026-10-20: re-entry guard — a double tap scheduled two nextStep() timeouts and skipped a question.
            if (this._advancing) return;
            this._advancing = true;
            // Second chance for the loader: an answered question is unambiguously past
            // LCP, and covers the auto-open case where openQuiz() fired pre-`load`.
            this.ensureKlaviyoLoaded();
            setTimeout(() => {
                this._advancing = false;
                if (this.engine.currentStep < this.engine.totalSteps - 1) {
                    this.engine.nextStep();
                    this.renderStep();
                } else {
                    this.renderEmailGate();
                }
            }, 400); // 400ms delay for visual feedback
        }

        // Force the lazy Klaviyo onsite loader (layout/theme.liquid) rather than waiting on
        // its generic interaction/idle heuristic. identify() and track() queue into _learnq
        // until klaviyo.js lands and drains them; a queue that has not drained before the
        // results CTA navigates away is lost. Someone in the quiz is about to generate a
        // high-value identify, so buy the loader the whole question sequence as head start.
        //
        // Never before the page `load` event: paid traffic auto-opens the drawer 500ms in
        // (?open-quiz=true), and pulling 430KB there would undo the facade's whole reason
        // for existing on exactly the pages whose LCP matters most.
        ensureKlaviyoLoaded() {
            const go = () => {
                try {
                    if (typeof window.AliangeLoadKlaviyo === 'function') window.AliangeLoadKlaviyo();
                } catch (e) { /* ignore */ }
            };
            if (document.readyState === 'complete') go();
            else window.addEventListener('load', go, { once: true });
        }

        openQuiz(entryPoint) {
            // One tap = one open (2026-10-20): ignore a re-entry while the drawer
            // is already open within 1.5s; the first opener's entry_point wins.
            if (this.drawer && this.drawer.getAttribute('aria-hidden') === 'false' &&
                Date.now() - (this._openedAt || 0) < 1500) return;
            this._openedAt = Date.now();
            // Reset per-open quiz_step dedupe (see fireQuizStep). Must run before
            // renderStep(), which fires quiz_step for the first question.
            this._stepsFired = {};
            this._advancing = false;
            // Stamped here, not in fireQuizStart(), because the dedupe guard returns
            // early on a repeat open from the same surface — the carry-through must
            // track every open, not only the ones that fire an event.
            if (entryPoint) {
                this._entryPoint = entryPoint;
                rememberQuizEntry(entryPoint);
            }
            this.ensureKlaviyoLoaded();
            this.drawer.setAttribute('aria-hidden', 'false');
            this.renderStep();
            this.fireQuizStart();
        }

        closeQuiz() {
            this.drawer.setAttribute('aria-hidden', 'true');
        }

        // GA4 quiz_start — (2026-10-20) gtag only: a gtag event routed through the
        // page's DEFAULT Google tag. The page's gtag snippet itself writes every gtag()
        // call into window.dataLayer, so the extra {event:...} object push showed each
        // event twice in dataLayer / Tag Assistant inspectors (no GTM container consumes
        // it, and GA4 only receives the gtag hit). See the send_to note in fireQuizStep.
        // Deduped on (GA4 session, entry_point): open/close/reopen from the SAME surface fires ONE
        // start, because the drawer always shows the first question on open, so opening
        // the drawer IS the first-question render. A re-open from a DIFFERENT surface
        // still fires — auto_open (shown) and drawer_pill (chose) are distinct
        // populations and collapsing them would destroy the "of the people we showed
        // the quiz to, how many finished" denominator the 8 Aug build set out to protect.
        fireQuizStart() {
            const entryPoint = this._entryPoint || 'drawer_other';
            const key = quizSessionKey();

            // GUARD (primary): cookie-backed, GA4-session-keyed.
            const prior = readQuizStartCookie();
            const sameSession = !!prior && prior.k === key;
            if (sameSession && prior.e.indexOf(entryPoint) !== -1) return;
            const entries = sameSession ? prior.e.concat([entryPoint]) : [entryPoint];
            const guarded = writeQuizStartCookie({ k: key, e: entries });

            // GUARD (fallback): only when the cookie jar refused the write. Keyed the
            // same way so behaviour is identical, just on the weaker store.
            if (!guarded) {
                const flag = 'aliange_quiz_started_' + key + '_' + entryPoint;
                try {
                    if (sessionStorage.getItem(flag) === '1') return;
                    sessionStorage.setItem(flag, '1');
                } catch (e) { /* no storage at all — fire once per load */ }
            }

            // HEDGE: independent store, so a guard failure is still recoverable in the
            // report. Never suppresses — it only labels.
            const openIndex = nextQuizOpenIndex(key);

            const params = {
                entry_point: entryPoint,
                quiz_version: QUIZ_VERSION,
                open_index: openIndex
            };
            if (typeof gtag === 'function') {
                gtag('event', 'quiz_start', { ...params });
            }
        }

        // GA4 quiz_step — diagnostic mid-quiz visibility. step_index capped at the
        // real question count, and kept OUT of headline funnel counts (it is depth,
        // not a funnel stage).
        // NOTE (2026-10-20): gtag only. The page's gtag snippet itself writes every
        // gtag() call into window.dataLayer, so the extra {event:...} object push
        // showed each event twice in dataLayer / Tag Assistant inspectors (no GTM
        // container consumes it, and GA4 only receives the gtag hit). quiz_step now
        // fires once per step_index per quiz open (a re-render of the same question,
        // e.g. double-tap auto-advance or back, does not refire).
        // NOTE (2026-08-02): do NOT add `send_to: 'G-G7MH1ENL7Z'` back to these gtag
        // events. That measurement ID has no client-side gtag config on this theme
        // (page_views reach GA4 via Littledata server-side + the content_for_header
        // Google tag), so addressing it directly with send_to spawns a context-less,
        // sourceless "phantom" session (empty landing page, ~100% bounce) — it caused
        // the 1 Aug "Unassigned +228%" spike. Sending WITHOUT send_to routes through
        // the page's default Google tag (exactly like the js_error event), which
        // already forwards to GA4 attached to the real session. See
        // reports/google-analytics/2026-08-02_unassigned-traffic-diagnostic.md.
        fireQuizStep() {
            const idx = this.engine.currentStep;
            if (idx < 0 || idx >= this.engine.totalSteps) return;
            const k = idx + 1;
            this._stepsFired = this._stepsFired || {};
            if (this._stepsFired[k]) return;
            this._stepsFired[k] = true;
            const params = { step_index: k, quiz_version: QUIZ_VERSION };
            if (typeof gtag === 'function') {
                gtag('event', 'quiz_step', { ...params });
            }
        }

        updateProgress() {
            const pct = ((this.engine.currentStep + 1) / (this.engine.totalSteps + 1)) * 100; // +1 for Email step
            this.progressBar.style.width = `${pct}%`;
        }

        renderStep() {
            const question = this.engine.getCurrentQuestion();

            const html = `
                <div class="question-container fade-in">
                    <span class="step-label">Step ${this.engine.currentStep + 1} of ${this.engine.totalSteps}</span>
                    <h2 class="question-text">${question.text}</h2>
                    <div class="options-grid">
                        ${question.options.map(opt => {
                const isSelected = this.engine.answers[question.id] === opt.id ? 'selected' : '';
                return `
                            <button class="option-btn ${isSelected}"
                                    data-question-id="${question.id}"
                                    data-option-id="${opt.id}">
                                ${opt.text}
                            </button>
                            `;
            }).join('')}
                    </div>
                </div>
            `;

            this.content.innerHTML = html;

            this.prevBtn.disabled = this.engine.currentStep === 0;
            this.prevBtn.textContent = "←"; // Arrow
            this.prevBtn.style.display = 'inline-block';
            // Next button is hidden via CSS

            this.updateProgress();
            this.fireQuizStep();
        }

        renderEmailGate() {
            // Hide progress bar or set to 90%?
            this.progressBar.style.width = '90%';

            const html = `
                <div class="email-capture-container fade-in">
                    <span class="step-label">Almost There</span>
                    <h2 class="question-text" style="margin-bottom: 24px;">Unlock your full skin analysis.</h2>
                    <p style="color: #666; margin-bottom: 32px;">Enter your email to save your profile and reveal your prescribed protocol.</p>

                    <div class="email-input-group">
                        <label for="user-email">Email Address</label>
                        <input type="email" id="user-email" class="email-input" placeholder="you@example.com" required aria-describedby="user-email-error">
                        <p id="user-email-error" role="alert" aria-live="polite" style="display: none; margin: 8px 0 0; font-size: 0.9em; color: #B3261E;"></p>
                    </div>

                    <div style="text-align: right;">
                         <button id="submit-email-btn" class="btn btn-primary" style="width: 100%;">Reveal My Protocol</button>
                    </div>

                    <p class="disclaimer-text">
                        By continuing, you agree to receive expert skin tips and updates from Aliangé. You can unsubscribe at any time.
                    </p>
                </div>
            `;

            this.content.innerHTML = html;
            this.prevBtn.style.display = 'none'; // No going back from email gate? Or maybe allowed. Let's hide to force forward.
            this.nextBtn.style.display = 'none';

            // Bind Submit
            const btn = document.getElementById('submit-email-btn');
            const input = document.getElementById('user-email');

            btn.addEventListener('click', () => {
                this.handleEmailSubmit(input.value);
            });

            // Allow Enter key
            input.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') this.handleEmailSubmit(input.value);
            });

            // Clear the inline error as soon as they edit the field.
            input.addEventListener('input', () => this.showEmailError(''));
        }

        // 2026-10-30 (CRO item 10): inline error instead of alert(). Native dialogs
        // are blocked or swallowed in Instagram/Facebook in-app browsers, so the
        // shopper saw nothing and could not tell why the gate would not open.
        showEmailError(message) {
            const errorEl = document.getElementById('user-email-error');
            const input = document.getElementById('user-email');
            if (errorEl) {
                errorEl.textContent = message;
                errorEl.style.display = message ? 'block' : 'none';
            }
            if (input) {
                if (message) input.setAttribute('aria-invalid', 'true');
                else input.removeAttribute('aria-invalid');
            }
        }

        handleEmailSubmit(email) {
            email = (email || '').trim();
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                this.showEmailError('Please enter a valid email address, for example you@example.com.');
                const input = document.getElementById('user-email');
                if (input) input.focus();
                return;
            }
            this.showEmailError('');

            const listId = this.engine.config.settings.klaviyo_list_id || "";
            if (!listId) {
                console.warn('[Aliangé Quiz] Klaviyo list ID not configured. Set it in the Quiz section settings.');
            }

            // Determine prescribed protocol BEFORE sending to Klaviyo
            const result = this.engine.determineResult();
            const scores = this.engine.scores; // Already calculated by determineResult

            // Universal first-party signal — read by the quiz popup to suppress on
            // subsequent visits (same device). 1y expiry. Mirrored as a Klaviyo profile
            // property below so cross-device server-side checks have it too.
            try {
                document.cookie = 'aliange_quiz_completed=1; max-age=' + (365 * 24 * 60 * 60) + '; path=/; SameSite=Lax';
            } catch (e) { /* ignore */ }

            // UTM attribution (additive — empty object when no campaign params present)
            const utmProps = getQuizUtmProps();

            // 1. Identify (Profile + Protocol on Profile)
            const profileData = {
                $email: email,
                $source: 'Skin Quiz',
                Quiz_Completed: true,
                Quiz_Completed_At: new Date().toISOString(),
                Prescribed_Protocol: result.title,
                Skin_Feel: this.engine.answers['baseline_skin'],
                Primary_Concern: this.engine.answers['primary_priority'],
                Reactivity: this.engine.answers['tolerance_reactivity'],
                Barrier_Integrity: this.engine.answers['barrier_integrity'],
                Ageing_Signs: this.engine.answers['structural_ageing'],
                Congestion: this.engine.answers['congestion_pattern'],
                Pigmentation: this.engine.answers['pigmentation'],
                Environment: this.engine.answers['environment'],
                ...utmProps
            };

            // 2. Track Event (For Flow Triggers)
            const eventData = {
                Prescribed_Protocol: result.title,
                Protocol_Handle: result.handle,
                Protocol_Price: result.price,
                Score_Barrier: scores.barrier,
                Score_Sensitivity: scores.sensitivity,
                Score_Pigment: scores.pigment,
                Score_Ageing: scores.ageing,
                Score_Acne: scores.acne,
                Score_Tolerance: scores.tolerance
            };

            // Support both modern Klaviyo API and legacy _learnq
            if (window.klaviyo) {
                // Modern Klaviyo API (newer snippet versions)
                window.klaviyo.identify(profileData);
                window.klaviyo.track('Completed Skin Quiz', eventData);
            } else if (window._learnq) {
                // Legacy Klaviyo API
                window._learnq.push(['identify', profileData]);
                window._learnq.push(['track', 'Completed Skin Quiz', eventData]);
            }

            const quizEventParams = {
                // entry_point + quiz_version added 2026-08-28: quiz_complete carried
                // neither, so completions could not be joined to their quiz_start
                // bucket. Both are already-registered event-scoped GA4 custom
                // dimensions (customDimensions/15365928512, /15365698136) so the
                // values simply start appearing — no GA4 config change. Completions
                // BEFORE this deploy read (not set) forever; expected, not a bug.
                entry_point: this._entryPoint || recallQuizEntry() || 'unknown',
                quiz_version: QUIZ_VERSION,
                recommended_protocol: result.handle,
                recommended_protocol_title: result.title,
                recommended_protocol_price: result.price,
                primary_concern: this.engine.answers['primary_priority'] || 'unknown',
                baseline_skin: this.engine.answers['baseline_skin'] || 'unknown',
                email_provided: !!email
            };

            // gtag only (2026-10-20): dropped the duplicate dataLayer object push,
            // same reason as quiz_start (see the fireQuizStep note above). No
            // send_to — routes through the page's default Google tag, same as
            // quiz_start/quiz_step and js_error.
            if (typeof gtag === 'function') {
                gtag('event', 'quiz_complete', {
                    ...quizEventParams
                });
            }

            // 3. Direct List Subscription + Profile Update (Klaviyo v3 client API)
            const publicKey = this.engine.config.settings.klaviyo_public_key || "";
            if (publicKey) {
                const headers = {
                    'Content-Type': 'application/vnd.api+json',
                    'revision': '2024-10-15'
                };

                // keepalive: true keeps these requests alive through the page navigation
                // the results CTA triggers — a plain fire-and-forget fetch is cancelled on
                // unload. navigator.sendBeacon() is the usual tool here but cannot set the
                // 'revision' / vnd.api+json headers Klaviyo's client API requires, so
                // keepalive is the correct form. Payload is well under the 64KB cap.

                // 3a. Upsert profile with all quiz properties
                fetch('https://a.klaviyo.com/client/profiles/?company_id=' + publicKey, {
                    method: 'POST',
                    keepalive: true,
                    headers: headers,
                    body: JSON.stringify({
                        data: {
                            type: 'profile',
                            attributes: {
                                email: email,
                                properties: {
                                    $source: 'Skin Quiz',
                                    Prescribed_Protocol: result.title,
                                    Skin_Feel: this.engine.answers['baseline_skin'],
                                    Primary_Concern: this.engine.answers['primary_priority'],
                                    Reactivity: this.engine.answers['tolerance_reactivity'],
                                    Barrier_Integrity: this.engine.answers['barrier_integrity'],
                                    Ageing_Signs: this.engine.answers['structural_ageing'],
                                    Congestion: this.engine.answers['congestion_pattern'],
                                    Pigmentation: this.engine.answers['pigmentation'],
                                    Environment: this.engine.answers['environment'],
                                    ...utmProps
                                }
                            }
                        }
                    })
                }).catch(() => {});

                // 3b. Subscribe to list
                if (listId) {
                fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + publicKey, {
                    method: 'POST',
                    keepalive: true,
                    headers: headers,
                    body: JSON.stringify({
                        data: {
                            type: 'subscription',
                            attributes: {
                                profile: {
                                    data: {
                                        type: 'profile',
                                        attributes: { email: email }
                                    }
                                }
                            },
                            relationships: {
                                list: {
                                    data: { type: 'list', id: listId }
                                }
                            }
                        }
                    })
                }).catch(() => {});
                }
            } else if (listId) {
                // Fallback: legacy endpoint (deprecated, may stop working). Kept only as a
                // last resort — with the public key now wired on both quiz surfaces this
                // branch should never run in production. Sends email + list only: no
                // protocol, no scores, no UTM.
                const formData = new FormData();
                formData.append('g', listId);
                formData.append('email', email);
                formData.append('$source', 'Skin Quiz');
                fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
                    method: 'POST',
                    keepalive: true,
                    body: formData
                }).catch(() => {});
            }

            // Move to Results immediately (don't block on network)
            this.showResults();
        }

        /**
         * Fetches real product data from Shopify and renders the result
         */
        showResults() {
            const result = this.engine.determineResult();

            // Show Loading State
            this.content.innerHTML = `
                <div style="display:flex; justify-content:center; align-items:center; height:300px;">
                    <div class="fade-in" style="text-align:center;">
                        <span style="font-size: 24px;">Analysing your skin...</span>
                    </div>
                </div>
            `;
            this.progressBar.style.width = '100%';
            this.nextBtn.style.display = 'none';
            this.prevBtn.style.display = 'none';

            // Fetch Real Product Data
            const handle = result.handle;

            fetch(`/products/${handle}.js`)
                .then(res => {
                    if (!res.ok) throw new Error("Product not found");
                    return res.json();
                })
                .then(product => {
                    this.renderResultCard(result, product);
                })
                .catch(() => {
                    // Fallback to static config data if fetch fails
                    this.renderResultCard(result, null);
                });
        }

        renderResultCard(configResult, shopifyProduct) {
            // Merge Data: Prefer Shopify Data if available
            const title = shopifyProduct ? shopifyProduct.title : configResult.title;
            // 2026-10-30: customer-facing Protocol name. The live product title reads
            // "Aliangé Protocols | Essential Protocol"; keep only the part after the bar.
            const protocolName = (String(title).split('|').pop() || '').trim() || configResult.title;
            const price = shopifyProduct ? (shopifyProduct.price / 100).toFixed(2) : configResult.price;
            const imageHtml = shopifyProduct ? `<img src="${shopifyProduct.featured_image}" alt="${title}" style="width:100%; border-radius: 4px; margin-bottom: 16px;">` : '';

            // First-order credit: static code, applied via /discount so the customer
            // never types it. Redirect lands them on their recommended protocol PDP
            // with the discount already in the cart session. Eligibility (first order
            // only) is enforced at checkout by the Shopify segment — no client check.
            const code = 'PRESCRIBE15';
            const offerUrl = '/discount/' + code + '?redirect=' + encodeURIComponent('/products/' + configResult.handle);
            // 2026-10-27 (CRO fixes 1-4): the main CTA now adds the protocol to cart,
            // then applies the code via /discount/<code>?redirect=/cart. offerUrl is
            // kept as the no-JS / failed-add fallback href.
            const cartDiscountUrl = '/discount/' + code + '?redirect=' + encodeURIComponent('/cart');
            // 2026-10-30 (CRO item 2): every result-card destination carries the code,
            // so a buyer who leaves via Step 1 or "View the full protocol" still has
            // PRESCRIBE15 in the cart session (and gets the first-order gift row).
            const withCode = (path) => '/discount/' + code + '?redirect=' + encodeURIComponent(path);
            const variant = shopifyProduct && Array.isArray(shopifyProduct.variants)
                ? (shopifyProduct.variants.find((v) => v.available) || shopifyProduct.variants[0])
                : null;
            const variantId = variant ? variant.id : null;
            const priceCents = variant ? variant.price : (shopifyProduct ? shopifyProduct.price : Math.round(parseFloat(configResult.price) * 100));
            // Same rounding as Shopify's percentage discount on a single line.
            const discountedCents = Math.round(priceCents * 0.85);
            const fmt = (c) => (c / 100).toFixed(2);
            const priceHtml = isFinite(discountedCents) && discountedCents > 0
                ? `<p style="margin: 0; font-size: 1.2em;"><s style="color: #888; font-weight: normal; margin-right: 8px;">$${fmt(priceCents)}</s><strong>$${fmt(discountedCents)} AUD</strong></p>
                       <p style="margin: 4px 0 0; font-size: 0.85em; color: #555;">With 15% off your first order.</p>
                       <p data-quiz-express-line style="margin: 2px 0 0; font-size: 0.85em; color: #555; white-space: nowrap;">Free Express shipping.</p>`
                : `<p style="font-weight: bold; font-size: 1.2em; margin: 0;">$${price} AUD</p>`;

            const html = `
                <div class="result-container fade-in">
                    <span class="eyebrow" style="color: grey; font-size: 0.9em; letter-spacing: 1px;">S K I N &nbsp; A N A L Y S I S</span>

                    <h2 style="font-size: 2.2em; margin: 16px 0 8px;">${title}</h2>

                    <div class="protocol-card" style="margin-bottom: 12px;">
                        ${priceHtml}
                    </div>

                    <a href="${offerUrl}" id="quiz-begin-protocol" class="btn btn-primary" style="display: block; width: 100%; box-sizing: border-box; text-align: center; text-decoration: none; margin-bottom: 12px;">Begin your protocol · 15% off applied</a>

                    <div class="quiz-offer" style="margin-bottom: 24px; background: #A4D2F1; padding: 16px 24px; text-align: left;">
                        <p style="margin: 0 0 6px; font-size: 0.78em; letter-spacing: 0.14em; text-transform: uppercase; color: #1A1A1A;">15% off your first order</p>
                        <button type="button" id="quiz-copy-code" aria-label="Copy code ${code}" style="display: inline-flex; align-items: center; gap: 10px; background: #F2F1EE; border: 1px solid #1A1A1A; color: #1A1A1A; font-family: monospace; font-size: 1.15em; letter-spacing: 0.12em; padding: 10px 16px; margin: 4px 0 12px; cursor: pointer;">
                            <span>${code}</span>
                            <span id="quiz-code-copied" style="font-family: inherit; font-size: 0.62em; letter-spacing: 0.08em; text-transform: uppercase; opacity: 0; transition: opacity 0.2s;">Copied</span>
                        </button>
                        <p style="margin: 0; font-size: 0.92em; color: #1A1A1A;">One use. Applied to your cart.</p>
                    </div>

                    ${imageHtml}

                    <p style="font-size: 1.1em; color: #555; margin-bottom: 24px;">${configResult.description}</p>

                    <div id="quiz-step1" style="margin-top: 20px;"></div>

                    <div style="margin-top: 20px; text-align: center;">
                        <a href="${offerUrl}" id="quiz-view-protocol" style="color: #666; text-decoration: underline; font-size: 0.9em;">View the full protocol</a>
                        <br>
                        <a href="#" id="quiz-retake" style="color: #999; text-decoration: underline; font-size: 0.85em; display: inline-block; margin-top: 10px;">Retake analysis</a>
                    </div>
                </div>
            `;

            this.content.innerHTML = html;

            // Copy code to clipboard, with a graceful fallback for older browsers.
            const copyBtn = document.getElementById('quiz-copy-code');
            if (copyBtn) {
                const legacyCopy = (text) => {
                    try {
                        const ta = document.createElement('textarea');
                        ta.value = text;
                        ta.style.position = 'fixed';
                        ta.style.opacity = '0';
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                    } catch (e) { /* ignore */ }
                };
                copyBtn.addEventListener('click', () => {
                    const confirmEl = document.getElementById('quiz-code-copied');
                    const showCopied = () => {
                        if (confirmEl) {
                            confirmEl.style.opacity = '1';
                            setTimeout(() => { confirmEl.style.opacity = '0'; }, 1600);
                        }
                    };
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(code).then(showCopied).catch(() => { legacyCopy(code); showCopied(); });
                    } else {
                        legacyCopy(code);
                        showCopied();
                    }
                    try {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({ event: 'quiz_code_copied', code: code });
                    } catch (e) { /* ignore */ }
                });
            }

            // Bare event, no send_to — pinning it spawns phantom GA4 sessions.
            // link_url (2026-10-30) records the destination of each result-card link.
            const trackResultClick = (target, linkUrl) => {
                try {
                    if (typeof gtag === 'function') {
                        gtag('event', 'quiz_result_click', { target: target, protocol_handle: configResult.handle, link_url: linkUrl || '' });
                    }
                } catch (e) { /* ignore */ }
            };

            // Step 1 entry price (2026-09-05). 44% of completers never opened a
            // product page: the only thing on the card was a $247-$621 protocol.
            // This offers the first step of that same protocol as a lower entry
            // point. 2026-10-30: routed through /discount so PRESCRIBE15 rides along.
            const step1Handle = configResult.step1_handle;
            const step1Slot = document.getElementById('quiz-step1');
            if (step1Handle && step1Slot) {
                const step1Url = withCode('/products/' + step1Handle);
                const paintStep1 = (stepTitle, stepPrice) => {
                    step1Slot.innerHTML = `
                        <p style="margin: 0 0 10px; font-size: 0.92em; color: #555;">Prefer to begin with one step? Step 1 of the ${protocolName} is the ${stepTitle}, $${stepPrice}.</p>
                        <a href="${step1Url}" id="quiz-step1-link" style="display: flex; align-items: center; justify-content: center; min-height: 44px; box-sizing: border-box; padding: 10px 18px; border: 1px solid #1A1A1A; color: #1A1A1A; text-decoration: none; font-size: 0.95em;">View the ${stepTitle}</a>`;
                    const l = document.getElementById('quiz-step1-link');
                    if (l) l.addEventListener('click', () => trackResultClick('step1', step1Url));
                };
                fetch('/products/' + step1Handle + '.js')
                    .then((r) => { if (!r.ok) throw new Error('step1_fetch_failed'); return r.json(); })
                    .then((p) => paintStep1(p.title.replace(/^Aliang\u00e9\s+/, ''), (p.price / 100).toFixed(2)))
                    .catch(() => { /* leave the slot empty rather than print a guessed price */ });
            }

            // Primary CTA (2026-10-27): add the protocol to cart, fire protocol_add
            // (same shape as the LP CTA handler, entry/atc_location = quiz_result),
            // then apply PRESCRIBE15 via /discount and land on /cart. If we have no
            // variant id or the add fails, the href (discount -> PDP) still works.
            const beginBtn = document.getElementById('quiz-begin-protocol');
            if (beginBtn) {
                let adding = false;
                beginBtn.addEventListener('click', (e) => {
                    if (!variantId) {
                        trackResultClick('cta_add', offerUrl);
                        return; // fallback: natural navigation to offerUrl
                    }
                    e.preventDefault();
                    if (adding) return;
                    adding = true;
                    beginBtn.style.opacity = '0.7';
                    beginBtn.style.pointerEvents = 'none';
                    trackResultClick('cta_add', cartDiscountUrl);
                    try {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({ event: 'quiz_offer_cta_click', protocol_handle: configResult.handle, destination: cartDiscountUrl });
                    } catch (err) { /* ignore */ }
                    const go = (url) => { window.location.href = url; };
                    fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify({ id: variantId, quantity: 1 })
                    })
                        .then((res) => { if (!res.ok) throw new Error('cart_add_failed'); return res.json().catch(() => ({})); })
                        .then((item) => {
                            item = item || {};
                            const itemPrice = typeof item.final_price === 'number' ? item.final_price / 100 : priceCents / 100;
                            const itemTitle = item.product_title || title;
                            let sent = false;
                            const next = () => { if (!sent) { sent = true; go(cartDiscountUrl); } };
                            try {
                                if (typeof gtag === 'function') {
                                    gtag('event', 'protocol_add', {
                                        protocol_handle: configResult.handle,
                                        protocol_title: itemTitle,
                                        variant_id: Number(variantId),
                                        value: itemPrice,
                                        currency: 'AUD',
                                        purchase_type: 'one_time',
                                        entry: 'quiz_result',
                                        atc_location: 'quiz_result',
                                        items: [{ item_id: String(variantId), item_name: itemTitle, price: itemPrice, quantity: 1 }],
                                        event_callback: next,
                                        event_timeout: 800
                                    });
                                }
                            } catch (err) { /* ignore */ }
                            setTimeout(next, 900);
                        })
                        .catch(() => { go(offerUrl); });
                });
            }

            const viewProtocolLink = document.getElementById('quiz-view-protocol');
            if (viewProtocolLink) viewProtocolLink.addEventListener('click', () => trackResultClick('protocol_link', offerUrl));

            const retakeBtn = document.getElementById('quiz-retake');
            if (retakeBtn) {
                retakeBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    window.location.reload();
                });
            }
        }
    }

    // --- 4. INITIALIZATION ---
    document.addEventListener('DOMContentLoaded', () => {
        // Shopify Integration: Override Config if AliangeSettings exists
        if (window.AliangeSettings) {

            // 1. Override Product Handles
            if (window.AliangeSettings.products) {
                QUIZ_CONFIG.protocols.forEach(p => {
                    // Map ID (e.g., 'the_protocol') to variable name in settings (e.g., 'the_protocol')
                    // Note: Settings keys match IDs roughly but let's be safe.
                    // Settings: essential, the_protocol, pm, anti_ageing, acne, am
                    // Protocol IDs: essential, the_protocol, pm_protocol, anti_ageing, acne, am_protocol

                    let settingKey = p.id;
                    if (p.id === 'pm_protocol') settingKey = 'pm';
                    if (p.id === 'am_protocol') settingKey = 'am';

                    if (window.AliangeSettings.products[settingKey]) {
                        p.handle = window.AliangeSettings.products[settingKey];
                    }
                });
            }

            // 2. Override Klaviyo List + Public Key
            if (window.AliangeSettings.klaviyoListId) {
                QUIZ_CONFIG.settings.klaviyo_list_id = window.AliangeSettings.klaviyoListId;
            }
            if (window.AliangeSettings.klaviyoPublicKey) {
                QUIZ_CONFIG.settings.klaviyo_public_key = window.AliangeSettings.klaviyoPublicKey;
            }
        }

        const engine = new QuizEngine(QUIZ_CONFIG);
        const ui = new QuizUI(engine, {
            drawer: '#quiz-drawer',
            content: '#quiz-content',
            progressBar: '#progress-bar',
            nextBtn: '#next-btn',
            prevBtn: '#prev-btn',
            closeBtn: '#close-quiz-btn',
            startBtn: '#start-quiz-btn'
        });

        ui.init();

        // Early tap on [data-open-quiz], before this listener existed. The hero
        // section (aliange-lp-hero.liquid) sets window.__aliangeQuizPending and
        // does not call openQuiz itself, so this is the only open for that click.
        if (window.__aliangeQuizPending) {
            const pendingQuizEntry = window.__aliangeQuizPending;
            window.__aliangeQuizPending = '';
            ui.openQuiz(pendingQuizEntry);
        }

        // Expose for debugging. Assigned after the pending open so a click that
        // arrives once this handler yields sees AliangeQuiz and takes the
        // listener path, not a second pending open.
        window.AliangeQuiz = { engine, ui };
    });

})();
